import { useEffect, useRef, useState } from "react";

/**
 * Marca da entrada extra que o formulário põe no histórico do navegador.
 *
 * Os formulários de disparo e de template não são rota: são troca de view
 * dentro da tela de disparos, e o app usa `BrowserRouter`, sem o `useBlocker`
 * do data router. Sem esta entrada, o "voltar" do navegador saía da tela e
 * levava o preenchimento junto. Com ela, o "voltar" cai numa entrada de mesma
 * URL, o `popstate` chega aqui e o formulário decide: volta para a lista ou,
 * havendo alteração, pergunta antes.
 */
const SENTINELA = "formularioAberto";
// Remoção da entrada adiada um tick: o StrictMode desmonta e remonta o
// componente em seguida, e a remontagem cancela o timer em vez de empilhar
// outra entrada.
let timerSentinela: number | undefined;

type Opcoes<T> = {
  draft: T;
  /** Falso em modo de visualização: sem edição, nunca há o que perder. */
  ativo: boolean;
  onBack: () => void;
  /**
   * Falso quando o formulário abre por cima de outro (janela): a entrada do
   * histórico é do formulário de baixo, e mexer nela faria o "voltar" dele.
   */
  historico?: boolean;
};

/**
 * Sair de um formulário com alteração não salva pede confirmação — pelos
 * botões Voltar/Cancelar (`pedirSaida`) e pelo "voltar" do navegador (ver
 * SENTINELA). Recarregar ou fechar a aba cai no aviso do próprio navegador,
 * que não aceita texto próprio.
 *
 * Alteração é o rascunho diferente do que era na primeira interação (toque,
 * tecla ou foco — `capturas`, no contêiner do formulário), não na montagem:
 * ao montar, o próprio formulário ajusta o rascunho sozinho (o remetente
 * legado vira a opção do catálogo, no CanalCard), e comparar com a montagem
 * acusaria alteração num cadastro que ninguém tocou. Desfazer a mudança à mão
 * volta a não haver alteração.
 */
export function useSaidaProtegida<T>({ draft, ativo, onBack, historico = true }: Opcoes<T>) {
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const referencia = useRef<string | null>(null);
  // Entre zerarReferencia e a renderização seguinte o rascunho novo ainda não
  // chegou: um foco nesse meio (o da abertura de uma janela, por exemplo)
  // marcaria a referência com o rascunho velho, e o Cancelar acusaria
  // alteração num formulário que ninguém tocou.
  const recarregando = useRef(false);
  useEffect(() => {
    recarregando.current = false;
  });
  const marcarReferencia = () => {
    if (recarregando.current) return;
    if (referencia.current === null) referencia.current = JSON.stringify(draftRef.current);
  };
  const alterado = ativo && referencia.current !== null && JSON.stringify(draft) !== referencia.current;
  const alteradoRef = useRef(alterado);
  alteradoRef.current = alterado;

  const [confirmarSaida, setConfirmarSaida] = useState(false);
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;

  const pedirSaida = () => {
    if (alteradoRef.current) setConfirmarSaida(true);
    else onBack();
  };

  useEffect(() => {
    if (!historico) return;
    window.clearTimeout(timerSentinela);
    const empilhar = () =>
      window.history.pushState({ ...window.history.state, [SENTINELA]: true }, "");
    if (!window.history.state?.[SENTINELA]) empilhar();

    const onPop = () => {
      // O "voltar" já consumiu a entrada. Com alteração, ela volta para a
      // pilha — o formulário continua onde está até a pessoa confirmar.
      if (alteradoRef.current) {
        empilhar();
        setConfirmarSaida(true);
      } else {
        onBackRef.current();
      }
    };
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      // Saiu por Salvar, Cancelar ou confirmação: a entrada ainda está no
      // topo e sai, para o próximo "voltar" não cair numa página igual. Se
      // quem desmontou foi uma navegação do app, o topo já é outra entrada
      // e nada muda.
      timerSentinela = window.setTimeout(() => {
        if (window.history.state?.[SENTINELA]) window.history.back();
      }, 0);
    };
  }, []);

  useEffect(() => {
    if (!alterado) return;
    const avisar = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", avisar);
    return () => window.removeEventListener("beforeunload", avisar);
  }, [alterado]);

  return {
    /** Espalhar no contêiner do formulário. */
    capturas: {
      onPointerDownCapture: marcarReferencia,
      onKeyDownCapture: marcarReferencia,
      onFocusCapture: marcarReferencia,
    },
    /** O rascunho foi recarregado (troca de modo ou de registro): nova referência. */
    zerarReferencia: () => {
      referencia.current = null;
      recarregando.current = true;
    },
    pedirSaida,
    /** Props do ConfirmDialog de "Sair sem salvar?". */
    dialogo: {
      isOpen: confirmarSaida,
      onClose: () => setConfirmarSaida(false),
      onConfirm: () => {
        setConfirmarSaida(false);
        onBack();
      },
    },
  };
}
